import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Badge, Button, Modal } from '@lipaflow/ui'
import { signupsService, type SignupCounts } from '../../services/signups'

const ROWS: { key: keyof SignupCounts; label: string }[] = [
  { key: 'submitted', label: 'Signed up' },
  { key: 'opened_automatically', label: 'Opened automatically' },
  { key: 'waiting_for_review', label: 'Waiting for a person' },
  { key: 'confirmed_email', label: 'Confirmed their email' },
  { key: 'went_live', label: 'Went live' },
]

/**
 * A sector's Start free sign-ups at a glance, with its emergency brake: when
 * it is on, every new sign-up for the sector waits here for a person instead
 * of opening on its own. The other sector's sign-ups are not affected.
 */
export function SignupsPanel({ sector, sectorName }: { sector: string; sectorName: string }) {
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = useState(false)
  const [reason, setReason] = useState('')
  const { data } = useQuery({
    queryKey: ['signups-summary', sector],
    queryFn: () => signupsService.summary(sector, 7),
    refetchInterval: 60_000,
  })
  const brake = useMutation({
    mutationFn: (enabled: boolean) => signupsService.setManualReview(sector, enabled, reason.trim()),
    onSuccess: (result) => {
      toast.success(
        result.manual_review
          ? `Every new ${sectorName} sign-up now waits for review`
          : `${sectorName} sign-ups open automatically again`,
      )
      setConfirming(false)
      setReason('')
      queryClient.invalidateQueries({ queryKey: ['signups-summary'] })
    },
    onError: () => toast.error('Could not change it. Try again.'),
  })
  if (!data) return null
  const turningOn = !data.manual_review

  return (
    <section className="rounded-card border border-hairline bg-surface p-5" aria-labelledby="signups-heading">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="signups-heading" className="font-outfit text-h4 text-ink">{sectorName} sign-ups</h2>
          <p className="text-sm text-ink-muted">
            {data.last_hour} in the last hour
            {data.spike && (
              <Badge variant="warning" size="sm" className="ml-2">
                Unusually busy (over {data.spike_threshold_per_hour} an hour)
              </Badge>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {data.manual_review ? (
            <Badge variant="warning">Every sign-up waits for review</Badge>
          ) : (
            <Badge variant="success">Opening automatically</Badge>
          )}
          <Button size="sm" variant={turningOn ? 'danger' : 'secondary'} onClick={() => setConfirming(true)}>
            {turningOn ? 'Send all to review' : 'Open automatically again'}
          </Button>
        </div>
      </div>

      <table className="mt-4 w-full text-left text-sm">
        <thead>
          <tr className="border-b border-hairline text-ink-muted">
            <th scope="col" className="py-2 font-medium" />
            <th scope="col" className="py-2 text-right font-medium">Last 24 hours</th>
            <th scope="col" className="py-2 text-right font-medium">Last {data.period_days} days</th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map((row) => (
            <tr key={row.key} className="border-b border-hairline last:border-0">
              <th scope="row" className="py-2 font-normal text-ink">{row.label}</th>
              <td className="tabular py-2 text-right text-ink">{data.last_24_hours[row.key]}</td>
              <td className="tabular py-2 text-right text-ink">{data.period[row.key]}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="mt-4 text-sm text-ink-muted">
        {data.setup.in_setup} workspace{data.setup.in_setup === 1 ? '' : 's'} in setup
        {data.setup.past_warning_day > 0 && <>, {data.setup.past_warning_day} warned they will be removed</>}
        {data.setup.offboarding_awaiting_approval > 0 && (
          <>
            , <strong className="text-ink">{data.setup.offboarding_awaiting_approval} waiting for you to approve switching off</strong>{' '}
            (Support → Offboarding)
          </>
        )}
        .
      </p>

      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title={turningOn ? 'Send every new sign-up to review?' : 'Open sign-ups automatically again?'}
      >
        <p className="text-sm text-ink-muted">
          {turningOn
            ? 'New sign-ups will wait in Verifications until someone approves them. Use this during an attack or a spike you do not recognise.'
            : 'Sign-ups that pass the checks will open on their own again.'}
        </p>
        <label htmlFor="brake-reason" className="mt-4 block text-sm font-medium text-ink">Reason (kept in the activity log)</label>
        <input
          id="brake-reason" value={reason} onChange={(e) => setReason(e.target.value)}
          className="mt-1 w-full rounded-button border border-hairline bg-surface px-3 py-2 text-ink outline-none focus:border-accent"
          placeholder={turningOn ? 'e.g. 60 sign-ups an hour from one network' : 'e.g. spike has passed'}
        />
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setConfirming(false)}>Cancel</Button>
          <Button
            variant={turningOn ? 'danger' : 'primary'}
            disabled={reason.trim().length < 3 || brake.isPending}
            isLoading={brake.isPending}
            onClick={() => brake.mutate(turningOn)}
          >
            {turningOn ? 'Send all to review' : 'Open automatically'}
          </Button>
        </div>
      </Modal>
    </section>
  )
}
